import { gsap } from "gsap";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { IAuthController } from "../../controllers/IAuthController";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { VBox } from "../../../../common/components/ui/VBox";
import { MailButton } from "../components/MailButton";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { TextWithUnderline } from "by/gamefactory/cityversetycoon/scenes/game/components/TextWithUnderline";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";
import { PasskeyLoginButton } from "../components/PasskeyLoginButton";

export class MainLoginPopupView extends BaseScalableView<void> {
    private static readonly BY_SIGN_TEXT = "By connect with email/passkey";
    private static readonly I_ACCEPT_TEXT = "I accept & agree to the";
    private static readonly TERMS_OF_USE_TEXT = "Terms of Use";

    private readonly loginResources: LoginResourcePackage;
    private readonly authController: IAuthController;
    private readonly analyticsProvider: IAnalyticProviderWrapper;

    private background: Sprite;
    private logo: Sprite;
    private mailLoginButton: MailButton;
    private passkeyLoginButton: PasskeyLoginButton;

    private buttonsBox: VBox;
    private passKeyButtonsBox: VBox;
    private bySignText: Text;
    private iAcceptText: Text;
    private termsOfUseText: TextWithUnderline;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        loginResources: LoginResourcePackage,
        canvasScaler: ICanvasScaler,
        authController: IAuthController,
        analyticsProvider: IAnalyticProviderWrapper
    ) {
        super(operationControls, canvasScaler);
        this.loginResources = loginResources;
        this.authController = authController;
        this.analyticsProvider = analyticsProvider;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    private build(): void {
        this.background = new Sprite(this.loginResources.assets_views_login_main_login_screen_main_login_background_png);
        this.background.eventMode = "static";
        this.addChild(this.background);

        this.logo = new Sprite(this.loginResources.assets_views_login_main_login_screen_login_logo_png);
        this.addChild(this.logo);

        this.passkeyLoginButton = new PasskeyLoginButton(this.loginResources);
        this.passkeyLoginButton.onUp.add(this.passkeyButtonClickHandle, this);

        this.passKeyButtonsBox = new VBox(20);
        this.passKeyButtonsBox.addChild(this.passkeyLoginButton);
        this.addChild(this.passKeyButtonsBox);

        this.mailLoginButton = new MailButton(this.loginResources);
        this.mailLoginButton.onUp.add(this.mailButtonClickHandle, this);

        this.buttonsBox = new VBox(20);
        this.buttonsBox.addChild(this.mailLoginButton);
        this.addChild(this.buttonsBox);

        this.bySignText = new Text(MainLoginPopupView.BY_SIGN_TEXT, new DescriptionTextStyle());
        this.addChild(this.bySignText);

        this.iAcceptText = new Text(MainLoginPopupView.I_ACCEPT_TEXT, new DescriptionTextStyle());
        this.addChild(this.iAcceptText);

        this.termsOfUseText = new TextWithUnderline(MainLoginPopupView.TERMS_OF_USE_TEXT, new TermsOfUseTextStyle(), 0, -3, 2);
        this.termsOfUseText.eventMode = "static";
        this.termsOfUseText.on("pointerup", this.termsOfUseClickHandle, this);
        this.addChild(this.termsOfUseText);
    }

    private async mailButtonClickHandle(): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email button click", this.analyticsProvider.currentEventIndex));
        const result = await this.authController.showEmailAuthPopup();
        if (result) {
            await this.authController.showSignInSuccessfulPopup();
            await this.hideView();
            this.operationControls.complete();
        }
    }

    private async passkeyButtonClickHandle(): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Passkey button click", this.analyticsProvider.currentEventIndex));

        const result = await this.authController.showPasskeyAuthPopup();
        if (result) {
            await this.authController.showSignInSuccessfulPopup();
            await this.hideView();
            this.operationControls.complete();
        }
    }

    private async termsOfUseClickHandle(): Promise<void> {
        this.authController.openTermsOfUse();
    }

    private async hideView(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.removeChild(inputBlocker);
    }

    private layout(width: number, height: number): void {
        this.background.width = width;
        this.background.height = height;
        this.buttonsBox.alignmentStrategy();
        this.buttonsBox.position.set((width - this.buttonsBox.width) * 0.5, 740);
        this.passKeyButtonsBox.alignmentStrategy();
        this.passKeyButtonsBox.position.set((width - this.passKeyButtonsBox.width) * 0.5, 880);
        this.bySignText.position.set((width - this.bySignText.width) * 0.5, this.passKeyButtonsBox.position.y + this.passKeyButtonsBox.height + 10);
        this.iAcceptText.position.set((width - this.iAcceptText.width - this.termsOfUseText.width) * 0.5, this.bySignText.y + this.bySignText.height);
        this.termsOfUseText.position.set(this.iAcceptText.position.x + this.iAcceptText.width + 3, this.iAcceptText.position.y);
        this.logo.position.set(159, 107);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.passkeyLoginButton.onUp.remove(this.passkeyButtonClickHandle, this);
        this.mailLoginButton.onUp.remove(this.mailButtonClickHandle, this);
        this.termsOfUseText.off("pointerup", this.termsOfUseClickHandle, this);
        super.destroy(options);
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT_REGULAR,
            fontSize: 33,
            letterSpacing: 1,
        });
    }
}

class TermsOfUseTextStyle extends DescriptionTextStyle {
    public constructor() {
        super();
        this.fontFamily = GameFont.DEFAULT_FONT;
        this.fill = "#00F9FC";
    }
}
